import { morph, executeMorph } from '@theme/morph';
import { debounce, createAbortController } from '@theme/utilities';

// Prefix used for Shopify section IDs in the DOM
const ID_PREFIX = 'shopify-section-';

/**
 * Renderer class responsible for managing section updates and caching
 * Handles fetching HTML content and coordinating morphing operations
 */
class Renderer {
  // Cache for storing HTML content to avoid unnecessary requests
  #htmlCache = new Map();
  // Map to track abort controllers for canceling in-flight requests
  #abortMap = new Map();
  // Map to track ongoing fetch requests to prevent duplicates
  #inFlight = new Map();

  constructor() {
    // Save initial section HTML to cache when page loads
    window.addEventListener('load', () => this.#saveInitialSections());
  }

  /**
   * Updates a section with new HTML content
   * @param {string} id - The section ID to update
   * @param {Object} opts - Options for the update
   * @param {boolean} opts.cache - Whether to use cached content (defaults to true in production)
   * @returns {Promise<string>} The fetched HTML markup
   */
  async update(id, opts = {}) {
    const useCache = opts.cache ?? !Shopify.designMode;

    // Cancel any active request for this section
    this.#cancelActive(id);

    // Create new abort controller for this request
    const controller = createAbortController(this.#abortMap.get(id));
    this.#abortMap.set(id, controller);

    // Fetch the HTML content
    const markup = await this.#fetchHTML(id, useCache);

    // Only proceed if request wasn't aborted
    if (!controller.signal.aborted) {
      this.#abortMap.delete(id);
      await executeMorph(id, markup);
    }

    return markup;
  }

  /**
   * Cancels any active request for a specific section
   * @param {string} id - The section ID to cancel
   */
  #cancelActive(id) {
    const controller = this.#abortMap.get(id);
    if (controller) controller.abort();
  }

  /**
   * Fetches HTML content for a section, with caching and deduplication
   * @param {string} id - The section ID to fetch
   * @param {boolean} fromCache - Whether to use cached content
   * @param {URL} base - Base URL for the request
   * @returns {Promise<string>} The HTML content
   */
  async #fetchHTML(id, fromCache = true, base = new URL(window.location.href)) {
    const path = generateURL(id, base);

    // Return existing in-flight request if one exists
    if (this.#inFlight.has(path)) return this.#inFlight.get(path);

    // Return cached content if available and requested
    if (fromCache && this.#htmlCache.has(path)) return this.#htmlCache.get(path);

    // Create new fetch request
    const fetchPromise = fetch(path).then(res => res.text());
    this.#inFlight.set(path, fetchPromise);

    // Wait for the request to complete
    const html = await fetchPromise;
    this.#inFlight.delete(path);
    this.#htmlCache.set(path, html);

    return html;
  }

  /**
   * Saves the initial HTML of all sections to cache on page load
   * This prevents unnecessary requests for sections that haven't changed
   */
  #saveInitialSections() {
    for (const el of document.querySelectorAll('.shopify-section')) {
      const url = generateURL(el.id);
      if (this.#htmlCache.has(url)) continue;
      if (hasShadow(el)) continue;

      this.#htmlCache.set(url, el.outerHTML);
    }
  }
}

/**
 * Generates a URL for fetching section content
 * @param {string} id - The section ID
 * @param {URL} base - Base URL (defaults to current page URL)
 * @returns {string} The generated URL with section_id parameter
 */
function generateURL(id, base = new URL(window.location.href)) {
  base.searchParams.set('section_id', stripPrefix(id));
  base.searchParams.sort();
  return base.toString();
}

/**
 * Creates a CSS selector for a section ID
 * @param {string} id - The section ID
 * @returns {string} The CSS selector
 */
function selector(id) {
  return `${ID_PREFIX}${id}`;
}

/**
 * Removes the Shopify section prefix from an ID
 * @param {string} id - The section ID with prefix
 * @returns {string} The section ID without prefix
 */
function stripPrefix(id) {
  return id.replace(new RegExp(`^${ID_PREFIX}`), '');
}

/**
 * Checks if a node or any of its children has a shadow root
 * @param {Node} node - The node to check
 * @returns {boolean} True if shadow root exists
 */
function hasShadow(node) {
  return !!node.shadowRoot || [...node.children].some(hasShadow);
}

// Export a singleton instance of the renderer
export const liveRenderer = new Renderer();
